import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, CheckCheck, ChevronRight, Loader2 } from 'lucide-react';
import { BottomSheet } from '@shared/ui/containers/BottomSheet';
import { Card } from '@shared/ui/containers/Card';
import { ListGroup } from '@shared/ui/lists/ListGroup';
import { ListItem } from '@shared/ui/lists/ListItem';
import { Button } from '@shared/ui/primitives/Button';
import { EmptyState } from '@shared/ui/feedback/EmptyState';
import { Banner } from '@shared/ui/feedback/Banner';
import { cn } from '@shared/lib/cn';
import { formatRelative } from '@shared/lib/format';
import {
  fetchNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  type Notification,
} from './api';
import { getNotificationTarget } from './notificationTargets';

interface NotificationsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function NotificationsSheet({ open, onOpenChange }: NotificationsSheetProps): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);

  const listQuery = useQuery({
    queryKey: ['notifications', 'list'],
    queryFn: () => fetchNotifications({ limit: 30 }),
    enabled: open,
  });

  const markReadMutation = useMutation({
    mutationFn: markNotificationRead,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const markAllMutation = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const items = listQuery.data ?? [];
  const unreadInList = items.filter((n) => !n.is_read).length;

  const subtitle =
    unreadInList > 0
      ? `${unreadInList} unread notification${unreadInList === 1 ? '' : 's'}`
      : "You're all caught up";

  return (
    <BottomSheet
      open={open}
      onOpenChange={onOpenChange}
      title="Notifications"
      description={subtitle}
      height="min(72dvh, 560px)"
    >
      {unreadInList > 0 ? (
        <div className="mb-4 flex justify-end">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={markAllMutation.isPending}
            onClick={() => markAllMutation.mutate()}
          >
            <CheckCheck className="h-4 w-4" strokeWidth={1.75} />
            Mark all read
          </Button>
        </div>
      ) : null}

      {listQuery.isLoading ? (
        <div className="flex items-center justify-center gap-2 py-12 text-body text-ink-secondary">
          <Loader2 className="h-5 w-5 animate-spin" strokeWidth={1.75} />
          Loading…
        </div>
      ) : null}

      {listQuery.isError ? (
        <Banner variant="danger" className="mb-4">
          Couldn't load notifications. Pull down to try again.
        </Banner>
      ) : null}

      {!listQuery.isLoading && !listQuery.isError && items.length === 0 ? (
        <EmptyState
          icon={<Bell className="h-10 w-10" strokeWidth={1.25} />}
          title="No notifications"
          description="When something needs your attention, it will show up here."
        />
      ) : null}

      {!listQuery.isLoading && items.length > 0 ? (
        <ListGroup className="pb-2">
          {items.map((item) => (
            <NotificationRow
              key={item.notification_id}
              notification={item}
              onOpen={() => {
                if (!item.is_read) {
                  markReadMutation.mutate(item.notification_id);
                }
                setSelectedNotification(item);
              }}
            />
          ))}
        </ListGroup>
      ) : null}

      <BottomSheet
        open={selectedNotification != null}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            setSelectedNotification(null);
          }
        }}
        title={selectedNotification?.title}
        description={selectedNotification ? formatRelative(selectedNotification.created_at) : undefined}
        height="min(70dvh, 520px)"
      >
        {selectedNotification ? (
          <NotificationDetail
            notification={selectedNotification}
            onOpenTarget={() => {
              const target = getNotificationTarget(selectedNotification);
              if (target?.kind === 'internal') {
                setSelectedNotification(null);
                onOpenChange(false);
                navigate(target.path);
                return;
              }

              if (target?.kind === 'external') {
                window.open(target.url, '_blank', 'noopener,noreferrer');
              }
            }}
          />
        ) : null}
      </BottomSheet>
    </BottomSheet>
  );
}

function NotificationRow({
  notification,
  onOpen,
}: {
  notification: Notification;
  onOpen: () => void;
}): JSX.Element {
  const subtitle = `${formatRelative(notification.created_at)} · ${notification.message}`;

  return (
    <ListItem
      title={notification.title}
      subtitle={subtitle}
      leading={
        <span
          className={cn(
            'mt-1 block h-2.5 w-2.5 rounded-full',
            notification.is_read ? 'bg-hairline-strong/70' : 'bg-primary-600',
          )}
          aria-hidden
        />
      }
      onClick={onOpen}
      className={cn(
        'min-h-[4.5rem]',
        notification.is_read ? 'hover:bg-sunken' : 'bg-primary-50/80 hover:bg-primary-50',
      )}
    />
  );
}

function NotificationDetail({
  notification,
  onOpenTarget,
}: {
  notification: Notification;
  onOpenTarget: () => void;
}): JSX.Element {
  const target = getNotificationTarget(notification);

  return (
    <div className="space-y-5 pb-4">
      <Card noGrain className="rounded-2xl bg-sunken/80 px-4 py-4 shadow-none">
        <p className="whitespace-pre-wrap text-body leading-7 text-ink">{notification.message}</p>
      </Card>

      {target ? (
        <Button type="button" variant="primary" size="md" className="w-full" onClick={onOpenTarget}>
          {target.kind === 'external' ? 'Open link' : 'Open related page'}
          <ChevronRight className="h-4 w-4" strokeWidth={1.75} />
        </Button>
      ) : null}
    </div>
  );
}
